/**
 * Entity Type Registry
 * 
 * Plugin-extensible registry for entity type UI metadata:
 * icons, labels, routes, and colors used across the graph,
 * chat context panels, relation editors, and navigation.
 * 
 * Core provides defaults; plugins register additional entity types
 * via registerEntityType() without contaminating core code.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getEntityProviders } from '../../api/platform';

// ── Types ───────────────────────────────────────────────────────────

export interface EntityTypeRegistration {
  /** Canonical entity type key (e.g., 'document', 'tag', 'event') */
  entityType: string;
  /** Emoji or icon component identifier */
  icon: string;
  /** Human-readable plural label (e.g., 'Documents', 'Records') */
  label: string;
  /** Frontend route path (e.g., '/documents', '/records') */
  route?: string;
  /** CSS color for graph visualization (hex or named) */
  color?: string;
  /** Plugin that registered this type (null = core default) */
  pluginId?: string;
  /** Sort order (lower = first) */
  order?: number;
  /** Mobile icon override (simpler glyph for small screens) */
  mobileIcon?: string;
  /** Route builder function — given an entity ID, returns the full path */
  routeBuilder?: (id?: string) => string;
  /** Whether a runtime data provider is attached */
  hasProvider?: boolean;
  /** Graph integration config from unified registry */
  graphConfig?: { autoSync?: boolean; nodeCategory?: string };
  /** RAG integration config from unified registry */
  ragConfig?: { enabled?: boolean; chunkStrategy?: string };
}

// ── Core Defaults ───────────────────────────────────────────────────
// Only infrastructure-level entity types that the core UI needs.
// All domain-specific entity types (documents, records, entities, etc.)
// are registered by plugins at load time via registerEntityType().

const CORE_ENTITY_TYPES: EntityTypeRegistration[] = [
  { entityType: 'chat', icon: '💬', label: 'Chats', route: '/chat', color: '#16a085', order: 0 },
  { entityType: 'file', icon: '📄', label: 'Files', color: '#95a5a6', order: 1 },
  { entityType: 'tag', icon: '🏷️', label: 'Tags', color: '#7f8c8d', order: 2 },
];

// ── Store ───────────────────────────────────────────────────────────

interface EntityTypeRegistryState {
  /** All registered entity types, keyed by entityType */
  entityTypes: Map<string, EntityTypeRegistration>;
  /** Track which plugins registered which types */
  pluginTypes: Map<string, Set<string>>;

  /** Register a single entity type (plugin or core) */
  registerEntityType: (registration: EntityTypeRegistration) => void;
  /** Register multiple entity types at once */
  registerEntityTypes: (registrations: EntityTypeRegistration[]) => void;
  /** Unregister all entity types from a specific plugin */
  unregisterPlugin: (pluginId: string) => void;

  /** Get icon for an entity type */
  getEntityIcon: (entityType: string) => string;
  /** Get label for an entity type */
  getEntityLabel: (entityType: string) => string;
  /** Get route for an entity type */
  getEntityRoute: (entityType: string) => string | undefined;
  /** Get color for an entity type */
  getEntityColor: (entityType: string) => string;
  /** Get full registration for an entity type */
  getEntityType: (entityType: string) => EntityTypeRegistration | undefined;
  /** Get all registered entity types */
  getAllEntityTypes: () => EntityTypeRegistration[];
  /** Get mobile icon for an entity type (falls back to regular icon) */
  getEntityMobileIcon: (entityType: string) => string;
  /** Get route for a specific entity instance by ID (uses routeBuilder if available) */
  getEntityRouteForId: (entityType: string, entityId?: string) => string | undefined;
  /** Get a lookup map for all icons (backwards compat) */
  getIconMap: () => Record<string, string>;
  /** Get a lookup map for all labels (backwards compat) */
  getLabelMap: () => Record<string, string>;
  /** Get a route map for all entity types */
  getRouteMap: () => Record<string, string>;
  /** Get all types that are synced to the knowledge graph */
  getGraphSyncedTypes: () => EntityTypeRegistration[];
  /** Get all types that are RAG-indexed */
  getRagEnabledTypes: () => EntityTypeRegistration[];
  /** Check if a type has a runtime data provider */
  hasProvider: (entityType: string) => boolean;

  /** Hydrate the store from the backend entity-provider endpoint */
  hydrateFromBackend: () => Promise<void>;
}

export const useEntityTypeRegistry = create<EntityTypeRegistryState>()(
  devtools(
    (set, get) => {
      // Initialize with core defaults
      const initialTypes = new Map<string, EntityTypeRegistration>();
      for (const reg of CORE_ENTITY_TYPES) {
        initialTypes.set(reg.entityType, reg);
      }

      return {
        entityTypes: initialTypes,
        pluginTypes: new Map(),

        registerEntityType: (registration) => {
          set((state) => {
            const newTypes = new Map(state.entityTypes);
            newTypes.set(registration.entityType, registration);

            const newPluginTypes = new Map(state.pluginTypes);
            if (registration.pluginId) {
              const existing = newPluginTypes.get(registration.pluginId) ?? new Set();
              existing.add(registration.entityType);
              newPluginTypes.set(registration.pluginId, existing);
            }

            return { entityTypes: newTypes, pluginTypes: newPluginTypes };
          });
        },

        registerEntityTypes: (registrations) => {
          set((state) => {
            const newTypes = new Map(state.entityTypes);
            const newPluginTypes = new Map(state.pluginTypes);

            for (const reg of registrations) {
              newTypes.set(reg.entityType, reg);
              if (reg.pluginId) {
                const existing = newPluginTypes.get(reg.pluginId) ?? new Set();
                existing.add(reg.entityType);
                newPluginTypes.set(reg.pluginId, existing);
              }
            }

            return { entityTypes: newTypes, pluginTypes: newPluginTypes };
          });
        },

        unregisterPlugin: (pluginId) => {
          set((state) => {
            const typesToRemove = state.pluginTypes.get(pluginId);
            if (!typesToRemove) return state;

            const newTypes = new Map(state.entityTypes);
            for (const entityType of typesToRemove) {
              // Restore core default if one exists, otherwise remove
              const coreDefault = CORE_ENTITY_TYPES.find(c => c.entityType === entityType);
              if (coreDefault) {
                newTypes.set(entityType, coreDefault);
              } else {
                newTypes.delete(entityType);
              }
            }

            const newPluginTypes = new Map(state.pluginTypes);
            newPluginTypes.delete(pluginId);

            return { entityTypes: newTypes, pluginTypes: newPluginTypes };
          });
        },

        getEntityIcon: (entityType) => {
          return get().entityTypes.get(entityType)?.icon ?? '📄';
        },

        getEntityLabel: (entityType) => {
          return get().entityTypes.get(entityType)?.label ?? entityType;
        },

        getEntityRoute: (entityType) => {
          return get().entityTypes.get(entityType)?.route;
        },

        getEntityColor: (entityType) => {
          return get().entityTypes.get(entityType)?.color ?? '#95a5a6';
        },

        getEntityType: (entityType) => {
          return get().entityTypes.get(entityType);
        },

        getAllEntityTypes: () => {
          return Array.from(get().entityTypes.values())
            .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
        },

        getEntityMobileIcon: (entityType) => {
          const reg = get().entityTypes.get(entityType);
          return reg?.mobileIcon ?? reg?.icon ?? '📄';
        },

        getEntityRouteForId: (entityType, entityId) => {
          const reg = get().entityTypes.get(entityType);
          if (!reg) return undefined;
          if (reg.routeBuilder) return reg.routeBuilder(entityId);
          if (reg.route && entityId) return `${reg.route}/${entityId}`;
          return reg.route;
        },

        getIconMap: () => {
          const map: Record<string, string> = {};
          for (const [key, reg] of get().entityTypes) {
            map[key] = reg.icon;
          }
          return map;
        },

        getLabelMap: () => {
          const map: Record<string, string> = {};
          for (const [key, reg] of get().entityTypes) {
            map[key] = reg.label;
          }
          return map;
        },

        getRouteMap: () => {
          const map: Record<string, string> = {};
          for (const [key, reg] of get().entityTypes) {
            if (reg.route) map[key] = reg.route;
          }
          return map;
        },

        getGraphSyncedTypes: () => {
          return Array.from(get().entityTypes.values())
            .filter((r) => r.graphConfig?.autoSync === true);
        },

        getRagEnabledTypes: () => {
          return Array.from(get().entityTypes.values())
            .filter((r) => r.ragConfig?.enabled === true);
        },

        hasProvider: (entityType) => {
          return get().entityTypes.get(entityType)?.hasProvider === true;
        },

        hydrateFromBackend: async () => {
          try {
            const data = await getEntityProviders();
            const registrations: EntityTypeRegistration[] = [];

            for (const entry of data.entityTypes) {
              // Skip types that are already registered as core defaults
              if (CORE_ENTITY_TYPES.some(c => c.entityType === entry.entityType)) {
                continue;
              }

              // A plugin's own UI registration (routes, icon, label) wins: replacing
              // it dropped route/routeBuilder, so every plugin entity link fell
              // back to the dead /entity/:id. The backend adds what it knows.
              const registered = Object.fromEntries(
                Object.entries(get().entityTypes.get(entry.entityType) ?? {}).filter(([, value]) => value !== undefined),
              ) as Partial<EntityTypeRegistration>;
              registrations.push({
                entityType: entry.entityType,
                icon: entry.icon ?? entry.providers[0]?.icon ?? '📦',
                label: entry.displayName ?? entry.providers[0]?.displayName ?? entry.entityType,
                color: entry.color,
                pluginId: entry.pluginId ?? entry.providers[0]?.pluginId,
                ...registered,
                hasProvider: entry.providers.length > 0,
                graphConfig: entry.graphConfig
                  ? { autoSync: entry.graphConfig.autoSync, nodeCategory: entry.graphConfig.nodeCategory }
                  : undefined,
                ragConfig: entry.ragConfig
                  ? { enabled: entry.ragConfig.enabled, chunkStrategy: entry.ragConfig.chunkStrategy }
                  : undefined,
              });
            }

            if (registrations.length > 0) {
              get().registerEntityTypes(registrations);
            }
          } catch (err) {
            // Non-critical — the UI still works with core defaults + plugin-registered types
            console.warn('[EntityTypeRegistry] Backend hydration failed (non-critical):', err);
          }
        },
      };
    },
    { name: 'entity-type-registry' },
  ),
);

// ── Convenience exports ─────────────────────────────────────────────

/** Get the icon emoji for an entity type. Shorthand for useEntityTypeRegistry.getState().getEntityIcon(). */
export function getEntityIcon(entityType: string): string {
  return useEntityTypeRegistry.getState().getEntityIcon(entityType);
}

/** Get the display label for an entity type. Shorthand for useEntityTypeRegistry.getState().getEntityLabel(). */
export function getEntityLabel(entityType: string): string {
  return useEntityTypeRegistry.getState().getEntityLabel(entityType);
}

/** Get the route path for an entity type. Shorthand for useEntityTypeRegistry.getState().getEntityRoute(). */
export function getEntityRoute(entityType: string): string | undefined {
  return useEntityTypeRegistry.getState().getEntityRoute(entityType);
}

/** Get the theme color for an entity type. Shorthand for useEntityTypeRegistry.getState().getEntityColor(). */
export function getEntityColor(entityType: string): string {
  return useEntityTypeRegistry.getState().getEntityColor(entityType);
}
